import { useState } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Icons,
} from '@/components';
import { getAnalysisDetail } from '@/services';
import { downloadFile, prepareMarkdown } from '@/utils';

import { BiasAnalysis, BiasAnalysisRecord, BiasedWord } from '@/types/common';

interface HistoryPanelProps {
    open: boolean;
    onClose: () => void;
    clearHistory: () => void;
    analysisHistory: BiasAnalysisRecord[];
}

export function HistoryPanel({
    analysisHistory,
    open,
    onClose,
}: HistoryPanelProps) {
    const [loadingId, setLoadingId] = useState<number | null>(null);

    const processTokenResults = (details: any): BiasAnalysis => {
        const biasedWords: BiasedWord[] = [];
        const labeledText: Record<string, string[]> = {};

        details.results.forEach((token: any, index: number) => {
            if (!token.labels || token.labels.length === 0) return;

            // Filter out 'O' labels which indicate no bias
            const biasLabels = token.labels.filter(
                (label: string) => label !== 'O'
            );

            if (biasLabels.length === 0) return;

            // Add to biased words list
            biasedWords.push({
                word: token.token,
                reasons: [`Identified as ${biasLabels.join(', ')}`],
                index: index,
                labels: biasLabels,
            });

            // Organize by label categories
            biasLabels.forEach((label: string) => {
                // Extract the category part (e.g., 'GEN' from 'B-GEN')
                const category = label.split('-').pop() || label;

                if (!labeledText[category]) {
                    labeledText[category] = [];
                }

                if (!labeledText[category].includes(token.token)) {
                    labeledText[category].push(token.token);
                }
            });
        });

        return {
            biasedWords,
            labeledText,
            prediction: details.results,
        };
    };

    const processLegacyResults = (details: any): BiasAnalysis => {
        const biasAnalysis: BiasAnalysis = {
            biasedWords: [],
            labeledText: {},
            prediction: {},
        };

        Object.entries(details.results).forEach(([category, result]) => {
            if (
                category.toLowerCase().includes('bias') &&
                typeof result === 'object'
            ) {
                if (Array.isArray(result)) {
                    biasAnalysis.biasedWords = result;
                }
                biasAnalysis.prediction = result;
            }

            if (
                typeof result === 'object' &&
                result !== null &&
                !Array.isArray(result)
            ) {
                Object.entries(result).forEach(([key, value]) => {
                    if (Array.isArray(value)) {
                        biasAnalysis.labeledText[key] = value as string[];
                    }
                });
            }
        });

        return biasAnalysis;
    };

    const parseAnalysisDetails = (details: any): BiasAnalysis => {
        // Process token-level results if available
        if (Array.isArray(details.results)) {
            return processTokenResults(details);
        }

        // Fallback to the previous implementation for backward compatibility
        if (typeof details.results === 'object' && details.results !== null) {
            return processLegacyResults(details);
        }

        // Return empty analysis if no valid results
        return {
            biasedWords: [],
            labeledText: {},
            prediction: {},
        };
    };

    const generateReport = (details: any, biasAnalysis: BiasAnalysis) => {
        // Prepare content for markdown generation
        const content = {
            currentText: details.text,
            normalizedSensitivity: details.sensitivity,
            biasAnalysis,
        };

        // Generate markdown using the structured approach from markdown.ts
        return prepareMarkdown(content);
    };

    const handleViewDetails = async (analysisId: number) => {
        try {
            setLoadingId(analysisId);
            const details = await getAnalysisDetail(analysisId);

            // Parse the analysis details into a structured format
            const biasAnalysis = parseAnalysisDetails(details);

            // Generate and download the report
            const markdownContent = generateReport(details, biasAnalysis);
            downloadFile(markdownContent);
        } catch (error) {
            console.error('Failed to fetch analysis details:', error);
        } finally {
            setLoadingId(null);
        }
    };

    const renderHistoryItem = (item: BiasAnalysisRecord) => {
        const truncatedText =
            item.text.length > 30
                ? `${item.text.substring(0, 30)}...`
                : item.text;

        const formattedDate = new Date(item.created_at).toLocaleString();
        const isLoading = loadingId === item.id;

        return (
            <div
                key={item.id}
                className="mb-4 p-4 border rounded flex justify-between"
            >
                <div>
                    <p className="font-semibold">{truncatedText}</p>
                    <p className="text-sm truncate">{formattedDate}</p>
                </div>

                <Button
                    onClick={() => handleViewDetails(item.id)}
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <Icons.loader className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                        <Icons.download className="h-4 w-4 mr-2" />
                    )}
                    View Details
                </Button>
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Analysis History</DialogTitle>
                </DialogHeader>
                <div className="mt-4 max-h-[400px] overflow-y-scroll">
                    {analysisHistory.length === 0 ? (
                        <p className="text-center">No history available</p>
                    ) : (
                        analysisHistory.map(renderHistoryItem)
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
